/**
 * CTA URL Validation Utilities
 * 
 * Provides security validation for CTA URLs to prevent:
 * - XSS attacks via javascript: URIs
 * - Data URI injection via data: URIs
 * - Protocol attacks (file://, etc.)
 * 
 * @module ctaValidation
 */

/**
 * Validate CTA URL to prevent security vulnerabilities
 * 
 * Blocks:
 * - javascript: URIs (XSS injection)
 * - data: URIs (data injection)
 * - file: URIs (file system access)
 * - Malformed URLs
 * 
 * Allows:
 * - http:// and https:// URLs
 * - Relative paths (/, /path)
 * - Mailto: links
 * 
 * @param url - URL to validate
 * @returns true if URL is safe, false otherwise
 * 
 * @example
 * ```typescript
 * isValidCtaUrl('https://calendly.com/qiksolve') // true
 * isValidCtaUrl('javascript:alert("XSS")') // false
 * isValidCtaUrl('data:text/html,<script>alert("XSS")</script>') // false
 * isValidCtaUrl('/booking') // true
 * isValidCtaUrl('mailto:hello@qiksolve.com') // true
 * ```
 */
export function isValidCtaUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false
  }
  
  const trimmedUrl = url.trim()
  
  // Block empty URLs
  if (trimmedUrl.length === 0) {
    return false
  }
  
  // Convert to lowercase for case-insensitive protocol checks
  const lowerUrl = trimmedUrl.toLowerCase()
  
  // Block dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'file:',
    'vbscript:',
  ]
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return false
    }
    
    // Also check URL-encoded variants (e.g., javascript%3A)
    const encodedProtocol = encodeURIComponent(protocol)
    if (lowerUrl.startsWith(encodedProtocol.toLowerCase())) {
      return false
    }
  }
  
  // Allow relative paths (start with /)
  if (trimmedUrl.startsWith('/')) {
    return true
  }
  
  // Allow mailto: links
  if (lowerUrl.startsWith('mailto:')) {
    return true
  }
  
  // Allow http/https URLs
  if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
    return true
  }
  
  // Block everything else (to be safe)
  return false
}

/**
 * Sanitize CTA URL with fallback
 * 
 * If URL is invalid, returns a fallback contact form URL.
 * Use this when you want graceful degradation instead of blocking rendering.
 * 
 * @param url - URL to sanitize
 * @param fallback - Fallback URL if validation fails (default: '/contact')
 * @returns Validated URL or fallback
 * 
 * @example
 * ```typescript
 * sanitizeCtaUrl('https://calendly.com/qiksolve') // 'https://calendly.com/qiksolve'
 * sanitizeCtaUrl('javascript:alert("XSS")') // '/contact'
 * sanitizeCtaUrl('invalid-url', '/booking') // '/booking'
 * ```
 */
export function sanitizeCtaUrl(url: string, fallback = '/contact'): string {
  return isValidCtaUrl(url) ? url : fallback
}
